import { useAuth, useOrganizationList } from '@clerk/clerk-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useEffect, ReactNode } from 'react';
import { Loader2 } from 'lucide-react';
import { AdminNoIndex } from '@/components/AdminNoIndex';

interface ProtectedRouteProps {
  children: ReactNode;
  requireOrganization?: boolean;
}

export function ProtectedRoute({ children, requireOrganization = true }: ProtectedRouteProps) {
  const { isSignedIn, isLoaded } = useAuth();
  const { userMemberships, isLoaded: membershipsLoaded } = useOrganizationList({
    userMemberships: {
      infinite: true,
    },
  });
  const navigate = useNavigate();
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith('/admin');

  useEffect(() => {
    if (isLoaded && !isSignedIn) {
      navigate('/auth');
      return;
    }

    if (requireOrganization && membershipsLoaded && isSignedIn) {
      const hasOrg = (userMemberships?.data?.length ?? 0) > 0;
      if (!hasOrg) {
        navigate('/create-organization');
      }
    }
  }, [isLoaded, isSignedIn, membershipsLoaded, userMemberships, navigate, requireOrganization]);

  if (!isLoaded || (requireOrganization && !membershipsLoaded)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!isSignedIn) {
    return null;
  }

  if (requireOrganization) {
    const hasOrg = (userMemberships?.data?.length ?? 0) > 0;
    if (!hasOrg) {
      return null;
    }
  }

  return (
    <>
      {isAdminRoute && <AdminNoIndex />}
      {children}
    </>
  );
}
